import type React from "react"
import { useState, useEffect, useRef } from "react"

interface ImageWithFallbackProps {
    src: string
    alt: string
    fallbackSrc?: string
    className?: string
    width?: string | number
    height?: string | number
    loading?: "lazy" | "eager"
    onLoad?: () => void
    onError?: () => void
    sizes?: string
    fetchpriority?: "high" | "low" | "auto"
    decoding?: "sync" | "async" | "auto"
}

/**
 * A component that displays an image with a fallback if the primary image fails to load
 * Enhanced for SEO with additional attributes
 */
const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
    src,
    alt,
    fallbackSrc = "/placeholder.svg",
    className = "",
    width,
    height,
    loading = "lazy",
    onLoad,
    onError,
    sizes = "100vw",
    fetchpriority = "auto",
    decoding = "async"
}) => {
    const [imgSrc, setImgSrc] = useState<string>(src)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [hasError, setHasError] = useState<boolean>(false)
    const imgRef = useRef<HTMLImageElement>(null)

    // Update image source when src prop changes
    useEffect(() => {
        setImgSrc(src)
        setIsLoading(true)
        setHasError(false)
    }, [src])

    // Check if image is already cached/complete
    useEffect(() => {
        const img = imgRef.current
        if (img && img.complete) {
            setIsLoading(false)
        }
    }, [imgSrc])

    const handleError = () => {
        if (!hasError) {
            setHasError(true)
            setImgSrc(fallbackSrc)
            setIsLoading(false)
            if (onError) onError()
        }
    }

    const handleLoad = () => {
        setIsLoading(false)
        if (onLoad) onLoad()
    }

    return (
        <div className={`relative ${className}`} style={{ width, height }}>
            {isLoading && (
                <div 
                    className="absolute inset-0 bg-pn_paper-3 animate-pulse rounded" 
                    style={{ width: '100%', height: '100%' }}
                />
            )}
            <img
                ref={imgRef}
                src={imgSrc || fallbackSrc}
                alt={alt}
                className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200 w-full h-full object-cover`}
                width={width}
                height={height}
                loading={loading}
                onError={handleError}
                onLoad={handleLoad}
                sizes={sizes}
                {...({ fetchpriority } as any)}
                decoding={decoding}
            />
        </div>
    )
}

export default ImageWithFallback